import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useResolveAlert, useSecurityAlerts, type SecurityAlert } from '@/hooks/data/useAdmin';
import { fetchMe } from '@/lib/auth';
import { formatDateTime } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/admin/security')({
  beforeLoad: async () => {
    const me = await fetchMe();
    if (!me || me.profile.role !== 'admin') throw redirect({ to: '/' });
  },
  component: AdminSecurityPage,
});

const SEVERITY_VARIANTS: Record<
  SecurityAlert['severity'],
  'destructive' | 'warning' | 'secondary'
> = {
  critical: 'destructive',
  warning: 'warning',
  info: 'secondary',
};

const TYPE_LABELS: Record<SecurityAlert['alert_type'], string> = {
  mass_edit: 'Massenänderung',
  phone_deletion: 'Telefon gelöscht',
  self_assignment_request: 'Selbst-Zuweisung',
  suspicious_login: 'Verdächtiger Login',
  ownership_violation: 'Eigentumsverletzung',
};

function AdminSecurityPage() {
  const { t } = useTranslation();
  const [showResolved, setShowResolved] = useState(false);
  const { data: alerts = [], isLoading } = useSecurityAlerts({
    resolved: showResolved ? undefined : false,
  });
  const resolve = useResolveAlert();

  const open = alerts.filter((a) => !a.resolved).length;
  const critical = alerts.filter((a) => a.severity === 'critical' && !a.resolved).length;

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {t('admin.security.title', 'Sicherheits-Alarme')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(
              'admin.security.subtitle',
              'Massenänderungen, Telefon-Löschungen, Selbst-Zuweisungen u.a.',
            )}
          </p>
        </div>
        <Button
          variant={showResolved ? 'default' : 'outline'}
          size="sm"
          onClick={() => setShowResolved((v) => !v)}
        >
          {showResolved ? t('admin.security.only_open', 'Nur offene') : t('admin.security.show_all', 'Alle anzeigen')}
        </Button>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        <Stat icon={<ShieldQuestion className="h-5 w-5" />} label="Offen" value={open} />
        <Stat icon={<ShieldAlert className="h-5 w-5 text-destructive" />} label="Kritisch" value={critical} />
        <Stat icon={<ShieldCheck className="h-5 w-5 text-success" />} label="Gesamt" value={alerts.length} />
      </div>

      <div className="space-y-2">
        {isLoading && <p className="text-sm text-muted-foreground">Lädt …</p>}
        {!isLoading && alerts.length === 0 && (
          <p className="text-sm text-muted-foreground">Keine Alarme.</p>
        )}
        {alerts.map((a) => (
          <div
            key={a.id}
            className={`flex items-start gap-3 rounded-2xl border p-3 ${
              a.resolved ? 'border-border/40 bg-background/40 opacity-70' : 'border-border bg-card shadow-soft'
            }`}
          >
            <Badge variant={SEVERITY_VARIANTS[a.severity]}>{a.severity}</Badge>
            <div className="flex-1">
              <p className="text-sm font-medium">{t(`admin.security.type.${a.alert_type}`, TYPE_LABELS[a.alert_type])}</p>
              <p className="text-xs text-muted-foreground">{a.description}</p>
              <p className="mt-1 text-[10px] text-muted-foreground/70">
                {formatDateTime(new Date(a.created_at))}
                {a.resolved && a.resolved_at
                  ? ` · gelöst ${formatDateTime(new Date(a.resolved_at))}`
                  : ''}
              </p>
            </div>
            {!a.resolved && (
              <Button size="sm" variant="outline" onClick={() => resolve.mutate(a.id)}>
                Auflösen
              </Button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-border bg-card/60 p-5 shadow-soft">
      <div className="rounded-lg bg-muted/40 p-2">{icon}</div>
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold tracking-tight">{value}</p>
      </div>
    </div>
  );
}
